/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { css, jsx } from '@atlaskit/css';
import { TOOLBAR_ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import {
	useSharedPluginState,
	sharedPluginStateHookMigratorFactory,
} from '@atlaskit/editor-common/hooks';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { ToolbarSeparator } from '@atlaskit/editor-common/ui';
import { ToolbarButton } from '@atlaskit/editor-common/ui-menu';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { WidthPlugin } from '@atlaskit/editor-plugin-width';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { fg } from '@atlaskit/platform-feature-flags';
import { LogoSvg as AtlassianIntelligenceLogo } from '@atlassian/generative-ai-modal/assets/Logo';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import { useEditorToolbarChangeDetector } from '../../hooks/useEditorToolbarChangeDetector';

type Props = {
	onClick: () => void;
	disabled: boolean;
	api?: PublicPluginAPI<[WidthPlugin]> | undefined;
	ButtonWrapper?: React.ComponentType<{ children: React.ReactNode }>;
	view?: EditorView;
};

const iconMinWidthStyle = css({
	minWidth: '24px',
});

const wrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
	marginLeft: 0,
	minWidth: 'auto',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- This rule thinks this isn't a `css()` call due to the name mapping
const wrapperStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div, > span': {
		display: 'flex',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div > div': {
		display: 'flex',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not([disabled])::after': {
			border: 'none', // remove blue border when an item has been selected
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			pointerEvents: 'none',
		},
	},
});

// This const is derived from the breakpoint where the toolbar hides its icons. It is used to hide the text in the AI button.
// Derived from values from platform/packages/editor/editor-core/src/ui/Appearance/FullPage/MainToolbar.tsx
const AI_BUTTON_WIDTH_BREAKPOINT = 1076;

const shouldShowSeparator = (node: HTMLSpanElement | null) => {
	if (!node) {
		return false;
	}

	// NOTE: Compiled CSS results in the previous sibling being a style tag rather then what was previously expected to be
	// another span/div tag. This is a temporary fix to ensure the separator is shown correctly.
	// As noted in the SSR compiled doco here (https://compiledcssinjs.com/docs/server-side-rendering) style elements are
	// inlined in the dom markup.
	const element =
		node.previousElementSibling?.tagName === 'STYLE'
			? node.previousElementSibling?.previousElementSibling
			: node.previousElementSibling;

	const prevSeparator = element?.querySelector(`:scope > span:last-child:empty`);
	const prevSeparatorVisible = prevSeparator && prevSeparator.clientWidth > 0;

	return !prevSeparatorVisible;
};
const useSharedState = sharedPluginStateHookMigratorFactory(
	(api: PublicPluginAPI<[WidthPlugin]> | undefined) => {
		const width = useSharedPluginStateSelector(api, 'width.width');
		return { width };
	},
	(api: PublicPluginAPI<[WidthPlugin]> | undefined) => {
		const { widthState } = useSharedPluginState(api, ['width']);
		return { width: widthState?.width };
	},
);

const WriteAIButton = ({
	api,
	view,
	onClick,
	isDisabled,
}: {
	onClick: () => void;
	api?: PublicPluginAPI<[WidthPlugin]> | undefined;
	view?: EditorView;
	isDisabled: boolean;
}) => {
	const { formatMessage } = useIntl();
	const title = formatMessage(sharedMessages.aiUniversalDescriptor);

	const { width } = useSharedState(api);

	const shouldShowLabel = width ? width >= AI_BUTTON_WIDTH_BREAKPOINT : true;

	return (
		<ToolbarButton
			onClick={onClick}
			buttonId={TOOLBAR_ACTION_SUBJECT_ID.AI}
			aria-label={title}
			testId="atlassian-intelligence-toolbar-button"
			title={title}
			iconBefore={
				<span css={iconMinWidthStyle}>
					{fg('platform-visual-refresh-icons') ? (
						<AtlassianIntelligenceIcon
							label="atlassian-intelligence-toolbar-button-icon"
							spacing="spacious"
						/>
					) : (
						<AtlassianIntelligenceLogo alt={''} isDisabled={isDisabled} />
					)}
				</span>
			}
			disabled={isDisabled}
		>
			{shouldShowLabel && formatMessage(sharedMessages.aiUniversalShortCallToAction)}
		</ToolbarButton>
	);
};

export const AtlassianIntelligenceToolbarButton = ({
	onClick,
	disabled,
	ButtonWrapper,
	api,
	view,
}: Props) => {
	const [showSeparator, setShowSeperator] = useState(false);
	const callbackRef = (node: HTMLSpanElement | null) => {
		const result = shouldShowSeparator(node);
		setShowSeperator(result);
	};

	const aiButton = <WriteAIButton api={api} view={view} onClick={onClick} isDisabled={disabled} />;
	const toolbarMutationCount = useEditorToolbarChangeDetector(ButtonWrapper);

	return (
		<span ref={callbackRef} css={[wrapperStyle, wrapperStylesUnbounded]}>
			{showSeparator && <ToolbarSeparator />}
			{ButtonWrapper ? (
				<ButtonWrapper key={`${toolbarMutationCount}-key-wrapper`}>{aiButton}</ButtonWrapper>
			) : (
				aiButton
			)}
		</span>
	);
};
